/**
 * Client-side panel session state — NOT the Expert record.
 *
 * The signed-in Expert lives in the TanStack Query cache and nowhere else. What
 * this store holds is the two things the cache cannot:
 *
 *   status       flipped to `signed_out` by the 401 handler, which runs OUTSIDE
 *                React (see ../lib/unauthenticated). It is reached by an idle
 *                session (US-01-005.3) and, just as often, by a Super Admin
 *                deactivating the Expert mid-session — `isActive` is re-checked
 *                on every authenticated request, so the rejection arrives on
 *                their very next call (US-01-005.17, US-00-006.15).
 *   destination  the panel screen a WhatsApp deep link was pointing at, held
 *                across OTP re-verification and used ONCE (US-01-001.22,
 *                US-01-004.15, US-01-005.24).
 *
 * The durable copy of `destination` is in @anthra/platform/session, which
 * survives a full page load; this is the in-memory mirror the router reads.
 */

import { create } from "zustand"

export type SessionStatus = "unknown" | "authenticated" | "signed_out"

type SessionState = {
	status: SessionStatus
	destination: string | null
	markAuthenticated: () => void
	markSignedOut: () => void
	setDestination: (path: string | null) => void
	/** Reads and clears in one call, so a destination cannot be used twice. */
	consumeDestination: () => string | null
}

export const useSessionStore = create<SessionState>()((set, get) => ({
	status: "unknown",
	destination: null,

	markAuthenticated: () => {
		set({ status: "authenticated" })
	},

	markSignedOut: () => {
		set({ status: "signed_out" })
	},

	setDestination: path => {
		set({ destination: path })
	},

	consumeDestination: () => {
		const { destination } = get()
		if (destination !== null) set({ destination: null })
		return destination
	},
}))
